/**
 * Detect whether the current page is running inside a third-party in-app
 * browser (Facebook, Instagram, LinkedIn, TikTok, etc.) and provide an escape
 * path that opens the same URL in the device's default browser.
 *
 * Why: Google blocks OAuth ("disallowed_useragent") inside embedded webviews,
 * so the only way to complete sign-in is to escape to a real browser first.
 */

const IN_APP_BROWSER_PATTERNS = [
  'FBAN', 'FBAV', 'FB_IAB', 'FBIOS',
  'Instagram',
  'Line/',
  'LinkedInApp',
  'Twitter',
  'MicroMessenger',
  'Snapchat',
  'musical_ly', 'Bytedance', 'TikTok',
  'KAKAOTALK',
  'NAVER',
  'Pinterest',
]

export function isInAppBrowser(userAgent: string = navigator.userAgent): boolean {
  return IN_APP_BROWSER_PATTERNS.some(pattern => userAgent.includes(pattern))
}

export function isIOS(userAgent: string = navigator.userAgent): boolean {
  return /iPhone|iPad|iPod/.test(userAgent)
}

export function isAndroid(userAgent: string = navigator.userAgent): boolean {
  return /Android/.test(userAgent)
}

/**
 * Specifically an iPhone, not "iOS". iPadOS 13+ reports a desktop-class
 * `Macintosh` UA (see {@link isIpadOsSafari}) and an iPod is not a phone, so
 * this matches neither — which is what a caller targeting an iPhone-only
 * control, like the Action Button, wants.
 */
export function isIPhone(userAgent: string = navigator.userAgent): boolean {
  return /iPhone/i.test(userAgent)
}

/**
 * Chrome on iPhone or iPad (`CriOS`). Google's step for adding a second
 * account fails there for most people, ending on a Google "400 … malformed"
 * page that never returns to us: from July to October 2026, 11 of 30 users
 * finished the personal Google connect from it, against 80-91% on every other
 * browser, and those who switched to Safari or a computer connected fine
 * (GET-19037). The add-inbox modal uses this to point them to Safari.
 */
export function isChromeOnIOS(userAgent: string = navigator.userAgent): boolean {
  return /CriOS\//.test(userAgent)
}

/**
 * iPadOS 13+ Safari reports a desktop-class UA (`Macintosh`, no `iPad` token),
 * so {@link isIOS} misses it. A real Mac reports `maxTouchPoints === 0` while an
 * iPad reports > 1 — use that to catch iPads, which would otherwise take the
 * popup OAuth path that's just as unreliable on iPadOS Safari as on iPhone.
 * Touch-point count isn't in the UA string, so this reads the live `navigator`
 * and no-ops in non-browser environments.
 */
function isIpadOsSafari(): boolean {
  return (
    typeof navigator !== 'undefined'
    && /Macintosh/.test(navigator.userAgent)
    && navigator.maxTouchPoints > 1
  )
}

/**
 * True on a mobile browser (iOS/iPadOS or Android), where popup-window OAuth
 * flows are unreliable: mobile Safari opens the blank popup as a detached tab,
 * severs `window.opener`, and no-ops `window.close()`, so an integration-connect
 * popup never reports back (GET-8963). Callers use this to switch to a full-page
 * redirect instead of a popup. The full-page redirect works on every browser, so
 * over-matching here (vs. a device whose popup would have worked) is harmless.
 */
export function isMobileBrowser(userAgent: string = navigator.userAgent): boolean {
  return isIOS(userAgent) || isAndroid(userAgent) || isIpadOsSafari()
}

/**
 * Build a URL that opens `targetUrl` in the system browser when the current
 * page is inside an in-app browser. Returns null when no escape strategy is
 * available for the current platform.
 *
 * iOS returns null: no programmatic escape works in 2026. The
 * `x-safari-https://` scheme is a private undocumented Safari-internal scheme
 * that Apple has never exposed to third-party apps — Instagram/Facebook
 * WKWebViews silently ignore it (PostHog showed iOS Instagram users
 * rage-tapping the escape button 22 and 15 times because nothing happened).
 * The `shortcuts://x-callback-url` workaround was killed in iOS 18.1. The
 * only thing that works on iOS is the manual menu → "Open in Browser"; the
 * LoginPage renders a visual instruction in lieu of an escape redirect.
 *
 * Android intents use `#` as a delimiter, so any URL fragment in `targetUrl`
 * would corrupt the intent. The fragment is stripped before encoding; today
 * we never pass one in but stripping here protects against future callers.
 *
 * If Chrome is missing on Android (rare but real on some EU/work-managed
 * devices), `S.browser_fallback_url` causes Android to load the original
 * `targetUrl` in whatever browser is the default rather than failing
 * silently back into the in-app browser.
 */
export function buildSystemBrowserUrl(targetUrl: string): string | null {
  const ua = navigator.userAgent
  if (isIOS(ua)) {
    return null
  }
  if (isAndroid(ua)) {
    // Android intents use `#` as the Intent-payload delimiter, so any URL
    // fragment must be stripped from the host portion. The fragmentless
    // URL is encoded back into S.browser_fallback_url so Android still
    // hands the full original URL to the fallback browser.
    const fragmentless = targetUrl.split('#')[0] ?? targetUrl
    const stripped = fragmentless.replace(/^https?:\/\//, '')
    const fallback = encodeURIComponent(fragmentless)
    return `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallback};end;`
  }
  return null
}

/**
 * Human-readable name of the system browser the escape will open. Used to
 * tailor the in-app-browser notice copy ("Safari" on iOS, "Chrome" on
 * Android) so the instruction matches what the user is about to see.
 */
export function systemBrowserName(userAgent: string = navigator.userAgent): string {
  if (isIOS(userAgent)) return 'Safari'
  if (isAndroid(userAgent)) return 'Chrome'
  return 'your default browser'
}

/**
 * Attempt to escape an in-app browser by navigating to the system-browser URL.
 * Returns true if an escape was attempted.
 */
export function tryEscapeInAppBrowser(targetUrl: string): boolean {
  const escapeUrl = buildSystemBrowserUrl(targetUrl)
  if (!escapeUrl) return false
  window.location.href = escapeUrl
  return true
}
